import { FC } from 'react';
import { EventWithMarketsAndSelections } from '../../sportsbook-types';
import { SportsbookTranslations } from '../../sportsbook-text-keys';
import { GameCard, GameCardList, GameCardListHeader, SkeletonGameCardList } from '../game-card';
import styled from 'styled-components';
import breakpoint from '../../../../../utils/style-utils';
import useEventsToMarketResolver from '../../hooks/use-events-to-market-resolver';

export const GameCardGroupsWrapper = styled.div`
	display: flex;
	flex-direction: column;
	gap: 12px;

	${breakpoint.lg`
		gap: 20px;
	`}
`;

const GameCardGroup = styled.div`
	border: 1px solid var(--generic-stroke-10-percent);
	border-radius: var(--base-radius-large);
	background-color: var(--main-surface-1);
`;

type Props = {
	title: string;
	titleLink?: string;
	tag: string;
	sportId?: string;
	events: EventWithMarketsAndSelections[];
	preferredMarketTypeIds?: string[];
	translations: SportsbookTranslations;
	onHeaderClick?: () => void;
	shouldClearSelectionTitle?: boolean;
	className?: string;
	sportIcon?: string;
};

/**
 * Component for displaying a group of events as game cards.
 *
 * @param title - The title of the group.
 * @param titleLink - The link for the title.
 * @param sportId - If all events has the same sport id, pass this id.
 * @param tag - The tag to determine origin of selection for tracking purposes.
 * @param events - The events to display.
 * @param preferredMarketTypeIds - Markets from dropdown selector.
 * @param translations - The translations.
 * @param onHeaderClick - Callback for when the header is clicked.
 * @param shouldClearSelectionTitle - Control if the selection renderer should clear the selection title and leave it empty
 * @param className - Optional override of styling
 */
const SportsbookGameCardGroup: React.FC<Props> = ({
	title,
	titleLink,
	tag,
	events,
	preferredMarketTypeIds,
	translations,
	onHeaderClick,
	shouldClearSelectionTitle,
	className,
	sportIcon
}) => {
	const { eventIdToMarket } = useEventsToMarketResolver(events, preferredMarketTypeIds);

	if (events.length === 0) {
		return null;
	}

	return (
		<GameCardGroup className={className}>
			<GameCardListHeader
				title={title}
				titleLink={titleLink}
				sportIcon={sportIcon}
				isExpanded
				onExpandToggle={onHeaderClick}
			/>
			<GameCardList>
				{events.map(event => (
					<GameCard
						key={event.id}
						event={event}
						market={eventIdToMarket[event.id]}
						shouldClearSelectionTitle={shouldClearSelectionTitle}
						translations={translations}
						tag={tag}
					/>
				))}
			</GameCardList>
		</GameCardGroup>
	);
};

type SkeletonProps = Pick<Props, 'title' | 'sportIcon'> & {
	eventsCount?: number;
};

export const SkeletonGameCardGroup: FC<SkeletonProps> = ({ title, eventsCount, sportIcon }) => {
	return (
		<GameCardGroup>
			<GameCardListHeader title={title} sportIcon={sportIcon} isExpanded />
			<SkeletonGameCardList itemCount={eventsCount} />
		</GameCardGroup>
	);
};

export default SportsbookGameCardGroup;
